/**
 * Utility to clean CPSC fields based on processing type.
 *
 * Transforms Object types to Input types and removes fields that are not
 * applicable for the selected processing type before form submission.
 * This ensures clean data is sent to the backend without extraneous fields.
 *
 * Key transformations:
 * - FacilityReference → IdInput (keeps only id)
 * - CpscEntity → CpscEntityInput
 * - CpscItem → CpscItemInput
 * - UsCpscPgaProgram → UsCpscPgaProgramInput
 */

import omit from 'lodash/omit';
import type {
  UsCpscPgaProgram,
  UsCpscPgaProgramInput,
  CpscItem,
  CpscItemInput,
  CpscEntity,
  CpscEntityInput,
  CpscProcessingType,
  IdInput,
  Facility,
} from '__generated__/graphql';
import { CpscProcessingType as ProcessingTypeEnum } from '__generated__/graphql';

// ============= FIELD REMOVAL CONSTANTS =============

/**
 * Fields to remove from root level for DISCLAIM processing type
 * DISCLAIM only needs: processingType, disclaimerCode, intendedUseDetails
 */
const DISCLAIM_REMOVE_ROOT_FIELDS = [
  'items',
  'certificateType',
  'certificateDeclaration',
  'globalIdentifier',
] as const;

/**
 * Fields to remove from root level for REF processing type
 * REF needs: processingType, intendedUseDetails, items
 */
const REF_REMOVE_ROOT_FIELDS = [
  'disclaimerCode',
  'certificateType',
  'certificateDeclaration',
  'globalIdentifier',
] as const;

/**
 * Fields to remove from root level for FULL processing type
 */
const FULL_REMOVE_ROOT_FIELDS = ['disclaimerCode'] as const;

/**
 * Fields to remove from items for REF processing type (FULL-only fields)
 * REF items only need: itemType, description, productRegistryReference
 */
const REF_REMOVE_ITEM_FIELDS = [
  'productName',
  'manufactureDate',
  'tradeName',
  'productCodes',
  'itemIdentifiers',
  'characteristics',
  'lotInformation',
  'labTestingInfo',
  'entities',
  'manufacturers',
  'laboratories',
  'certifyingEntities',
  'pointOfContacts',
] as const;

/**
 * Fields to remove from items for FULL processing type (REF-only fields)
 */
const FULL_REMOVE_ITEM_FIELDS = ['productRegistryReference'] as const;

// ============= ENTITY TRANSFORMATION =============

/**
 * Transform FacilityReference to IdInput
 * Strips all fields except id from federated facility object
 *
 * @param {Facility} facility - Facility object
 * @return {IdInput} IdInput with only id field
 */
const cleanFacility = (facility: Facility): IdInput => ({
  id: facility.id,
});

/**
 * Transform CpscEntity (Object type) to CpscEntityInput (Input type)
 * - Converts facility from FacilityReference to IdInput
 *
 * @param {CpscEntity} entity - CpscEntity from form/query
 * @return {CpscEntityInput} CpscEntityInput for mutation
 */
const cleanEntity = (entity: CpscEntity): CpscEntityInput => {
  const { facility, ...rest } = entity;
  return {
    ...rest,
    facility: facility ? cleanFacility(facility) : undefined,
  } as CpscEntityInput;
};

/**
 * Transform array of entities if present
 *
 * @param {CpscEntity[]} [entities] - Array of CpscEntity
 * @return {CpscEntityInput[] | undefined} Array of CpscEntityInput or undefined
 */
const cleanEntities = (
  entities?: CpscEntity[],
): CpscEntityInput[] | undefined => {
  if (!entities?.length) return undefined;
  return entities.map(cleanEntity);
};

// ============= ITEM TRANSFORMATION =============

/**
 * Clean item-level fields based on processing type.
 * Note: This function is only called for REF and FULL processing types.
 * DISCLAIM is filtered at the cleanCpscFields level.
 *
 * - Removes fields not applicable for the processing type
 * - Transforms entity arrays from Object to Input types (FULL only)
 *
 * @param {CpscItem} item - CpscItem to clean
 * @param {CpscProcessingType} processingType - REF or FULL processing type
 * @return {CpscItemInput} CpscItemInput ready for mutation
 */
const cleanItemFields = (
  item: CpscItem,
  processingType: CpscProcessingType,
): CpscItemInput => {
  // Get fields to remove based on processing type (REF or FULL only)
  const fieldsToRemove =
    processingType === ProcessingTypeEnum.REF
      ? REF_REMOVE_ITEM_FIELDS
      : FULL_REMOVE_ITEM_FIELDS;

  const cleaned = omit(item, fieldsToRemove);

  // Transform entity arrays (FULL only has entities)
  if (processingType === ProcessingTypeEnum.FULL) {
    return {
      ...cleaned,
      manufacturers: cleanEntities(cleaned.manufacturers ?? undefined),
      laboratories: cleanEntities(cleaned.laboratories ?? undefined),
      certifyingEntities: cleanEntities(
        cleaned.certifyingEntities ?? undefined,
      ),
      pointOfContacts: cleanEntities(cleaned.pointOfContacts ?? undefined),
    } as CpscItemInput;
  }

  return cleaned as CpscItemInput;
};

// ============= MAIN CLEAN FUNCTION =============

/**
 * Clean CPSC fields based on processing type.
 *
 * Transforms UsCpscPgaProgram (Object type) to UsCpscPgaProgramInput (Input type):
 * - Removes fields not applicable for the selected processing type
 * - Transforms nested entities from Object to Input types
 *
 *
 * @param {UsCpscPgaProgram} cpscData - CPSC form data to clean (must have processingType set)
 * @return {UsCpscPgaProgramInput} UsCpscPgaProgramInput with only applicable fields
 *
 * @example
 * const cleanedCpsc = cleanCpscFields(cpscFormData);
 */
export const cleanCpscFields = (
  cpscData: UsCpscPgaProgram,
): UsCpscPgaProgramInput => {
  const { processingType, items, ...restData } = cpscData;

  // Step 1: Remove root-level fields based on processing type
  let cleanedRoot: UsCpscPgaProgramInput;

  switch (processingType) {
    case ProcessingTypeEnum.DISCLAIM:
      cleanedRoot = omit(
        { processingType, ...restData },
        DISCLAIM_REMOVE_ROOT_FIELDS,
      ) as UsCpscPgaProgramInput;
      break;
    case ProcessingTypeEnum.REF:
      cleanedRoot = omit(
        { processingType, ...restData },
        REF_REMOVE_ROOT_FIELDS,
      ) as UsCpscPgaProgramInput;
      break;
    case ProcessingTypeEnum.FULL:
      cleanedRoot = omit(
        { processingType, ...restData },
        FULL_REMOVE_ROOT_FIELDS,
      ) as UsCpscPgaProgramInput;
      break;
    default:
      cleanedRoot = { processingType, ...restData };
  }

  // Step 2: Clean items if they exist (for REF and FULL)
  let cleanedItems: CpscItemInput[] | undefined;

  if (items && processingType !== ProcessingTypeEnum.DISCLAIM) {
    cleanedItems = items.map((item) => cleanItemFields(item, processingType));
  }

  // Step 3: Build final result
  return {
    ...cleanedRoot,
    ...(cleanedItems && { items: cleanedItems }),
  } as UsCpscPgaProgramInput;
};

export default cleanCpscFields;
